import { FormEvent, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_BOSS_FEEDBACK_POSTS,
  GET_BOSS_FEEDBACK_POST,
  CREATE_BOSS_FEEDBACK_REPLY,
  DELETE_BOSS_FEEDBACK_POST,
} from "../../../api/boss-feedback";
import { notify, notifyError } from "../../../utils/toast";
import { getErrorMessage } from "../../../utils/error";

type BossFeedbackPost = {
  id: string;
  content: string;
  authorName: string;
  authorId: number;
  status: string;
  createdAt: string;
  replyCount: number;
  replies: Array<{
    id: string;
    content: string;
    isAdmin: boolean;
    createdAt: string;
  }>;
};

const AdminBossFeedback: React.FC = () => {
  const { id } = useParams();
  const [selectedPostId, setSelectedPostId] = useState<string | null>(
    id || null,
  );
  const [reply, setReply] = useState("");

  const {
    data: postsData,
    loading: postsLoading,
    refetch: refetchPosts,
  } = useQuery(GET_BOSS_FEEDBACK_POSTS, {
    variables: { offset: 0, limit: 100 },
    skip: !!selectedPostId,
  });

  const { data: postData, refetch: refetchPost } = useQuery(
    GET_BOSS_FEEDBACK_POST,
    {
      variables: { id: selectedPostId ?? "" },
      skip: !selectedPostId,
    },
  );

  const [createReply, { loading: creating }] = useMutation(
    CREATE_BOSS_FEEDBACK_REPLY,
    {
      update: (cache, { data }, { variables }) => {
        if (!data?.createBossFeedbackReply || !variables?.input) return;
        const newReply = data.createBossFeedbackReply;
        const postId = variables.input.postId;
        try {
          const existingPost = cache.readQuery<{
            bossFeedbackPost: BossFeedbackPost;
          }>({
            query: GET_BOSS_FEEDBACK_POST,
            variables: { id: postId },
          });
          if (existingPost?.bossFeedbackPost) {
            cache.writeQuery({
              query: GET_BOSS_FEEDBACK_POST,
              variables: { id: postId },
              data: {
                bossFeedbackPost: {
                  ...existingPost.bossFeedbackPost,
                  replies: [...existingPost.bossFeedbackPost.replies, newReply],
                  replyCount: existingPost.bossFeedbackPost.replyCount + 1,
                  status: newReply.isAdmin
                    ? "ANSWERED"
                    : existingPost.bossFeedbackPost.status,
                },
              },
            });
          }
        } catch (e) {
          console.error("Cache update error:", e);
        }
        try {
          const existingPosts = cache.readQuery<{
            bossFeedbackPosts: BossFeedbackPost[];
          }>({
            query: GET_BOSS_FEEDBACK_POSTS,
            variables: { offset: 0, limit: 100 },
          });
          if (existingPosts?.bossFeedbackPosts) {
            const updatedPosts = existingPosts.bossFeedbackPosts.map((p) => {
              if (p.id === postId) {
                return {
                  ...p,
                  replies: [...(p.replies || []), newReply],
                  replyCount: p.replyCount + 1,
                  status: newReply.isAdmin ? "ANSWERED" : p.status,
                };
              }
              return p;
            });
            cache.writeQuery({
              query: GET_BOSS_FEEDBACK_POSTS,
              variables: { offset: 0, limit: 100 },
              data: { bossFeedbackPosts: updatedPosts },
            });
          }
        } catch (e) {
          console.error("Cache update error:", e);
        }
      },
      onCompleted: () => {
        setReply("");
        notify("답변이 등록되었습니다!");
        refetchPost();
        refetchPosts();
      },
      onError: (error) => {
        console.error("답변 작성 실패:", error);
        notifyError(`답변 작성 실패: ${getErrorMessage(error)}`);
      },
    },
  );

  const [deletePost, { loading: deleting }] = useMutation(
    DELETE_BOSS_FEEDBACK_POST,
    {
      onCompleted: (data: {
        deleteBossFeedbackPost: { ok: boolean; error?: string };
      }) => {
        if (data.deleteBossFeedbackPost.ok) {
          notify("게시글이 삭제되었습니다.");
          if (selectedPostId) setSelectedPostId(null);
          refetchPosts();
        } else {
          notifyError(
            data.deleteBossFeedbackPost.error || "게시글 삭제에 실패했습니다.",
          );
        }
      },
      onError: (error) => {
        console.error("게시글 삭제 실패:", error);
        notifyError(`게시글 삭제 실패: ${getErrorMessage(error)}`);
      },
    },
  );

  const handleDeletePost = (postId: string) => {
    if (!window.confirm("정말로 이 게시글을 삭제하시겠습니까?")) return;
    deletePost({
      variables: { id: parseInt(postId, 10) },
    });
  };

  const posts: BossFeedbackPost[] = useMemo(
    () => postsData?.bossFeedbackPosts || [],
    [postsData?.bossFeedbackPosts],
  );
  const post: BossFeedbackPost | undefined = postData?.bossFeedbackPost;
  const canSubmit = useMemo(
    () => reply.trim().length > 0 && !creating && !!selectedPostId,
    [reply, creating, selectedPostId],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPostId || !canSubmit) return;
    createReply({
      variables: {
        input: {
          postId: selectedPostId,
          content: reply.trim(),
        },
      },
    });
  };

  if (selectedPostId && post) {
    return (
      <div className="px-4 py-5">
        <button
          className="text-sm mb-3"
          onClick={() => {
            setSelectedPostId(null);
            setReply("");
          }}
        >
          ← 목록으로
        </button>
        <h1 className="text-lg font-semibold mb-4">사장님 의견 답변</h1>

        <div className="border rounded-lg p-4 mb-6 bg-white">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-gray-500">
              {post.authorName} (ID: {post.authorId}) ·{" "}
              {new Date(post.createdAt).toLocaleString()}
            </div>
            <button
              onClick={() => handleDeletePost(post.id)}
              disabled={deleting}
              className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
            >
              {deleting ? "삭제 중..." : "삭제"}
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap">{post.content}</p>
        </div>

        <div className="mb-6">
          <h2 className="text-sm font-medium mb-3">
            기존 답변 ({post.replies?.length ?? 0})
          </h2>
          {!post.replies?.length ? (
            <div className="text-sm text-gray-500">아직 답변이 없습니다.</div>
          ) : (
            <ul className="space-y-3">
              {post.replies.map((r) => (
                <li
                  key={r.id}
                  className={`border rounded-md p-3 ${
                    r.isAdmin
                      ? "bg-[#F4F5F7] border-[#1562fc]"
                      : "bg-white border-[#F4F5F7]"
                  }`}
                >
                  <div className="text-xs text-gray-500">
                    {r.isAdmin ? (
                      <span className="text-[#1562fc] font-medium">운영자</span>
                    ) : (
                      "사장님"
                    )}{" "}
                    · {new Date(r.createdAt).toLocaleString()}
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">
                    {r.content}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <form onSubmit={onSubmit} className="border-t pt-6">
          <div className="mb-3">
            <label className="text-sm font-medium mb-2 block">
              운영자 답변 작성
            </label>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="답변을 입력하세요"
              rows={6}
              className="w-full border border-[#F4F5F7] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1562fc]"
              disabled={creating}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-4 py-2 rounded-md text-sm ${
                canSubmit
                  ? "bg-[#1562fc] text-white hover:bg-[#124ab7]"
                  : "bg-[#F4F5F7] text-[#BBBDC0]"
              }`}
            >
              {creating ? "등록 중..." : "답변 등록"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="px-4 py-5">
      <h1 className="text-lg font-semibold mb-4">사장님 의견 관리</h1>

      {postsLoading ? (
        <div className="text-sm text-gray-500">로딩 중...</div>
      ) : posts.length === 0 ? (
        <div className="text-sm text-gray-500">아직 게시글이 없습니다.</div>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => {
            const hasReply = p.replyCount > 0;
            const hasAdminReply = p.replies?.some((r) => r.isAdmin) || false;
            const status = p.status?.toUpperCase();
            const isAnswered = status === "ANSWERED";
            return (
              <li key={p.id}>
                <div
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    hasReply
                      ? isAnswered || hasAdminReply
                        ? "bg-[#F4F5F7] border-[#1562fc]"
                        : "bg-[#F4F5F7] border-[#F4F5F7]"
                      : "bg-white border-[#1562fc]"
                  }`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => setSelectedPostId(p.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {p.authorName} (ID: {p.authorId}) ·{" "}
                        {new Date(p.createdAt).toLocaleString()}
                      </div>
                      {hasReply && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isAnswered || hasAdminReply
                              ? "bg-[#1562fc] text-white"
                              : "bg-[#F4F5F7] text-[#1562fc]"
                          }`}
                        >
                          {isAnswered || hasAdminReply
                            ? "✓ 답변 완료"
                            : `${p.replyCount}개 답변`}
                        </span>
                      )}
                      {!hasReply && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                          답변 필요
                        </span>
                      )}
                    </div>
                    {p.content && (
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                        {p.content}
                      </p>
                    )}
                  </button>
                  <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePost(p.id);
                      }}
                      disabled={deleting}
                      className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      {deleting ? "삭제 중..." : "삭제"}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminBossFeedback;
